/**
 * Role -> route/action permission map. The DB enforces these via RLS too;
 * this module is for UI-side guards (route gates, hidden buttons).
 *
 * Keep in sync with the `current_user_role()` helper and the per-table RLS
 * policies in supabase/migrations/.
 */

export type Role = 'super_admin' | 'admin' | 'manager' | 'employee' | 'external_lender';

export const ROLES: Role[] = [
  'super_admin',
  'admin',
  'manager',
  'employee',
  'external_lender',
];

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Employee',
  external_lender: 'External Lender',
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  super_admin:
    'Full access to everything, including financials, Mercury / QuickBooks data, integrations, and the audit log. Reviews and approves expense submissions.',
  admin:
    'Operations admin. NO access to financial reports, bank balances, or Mercury / QuickBooks. Sees own card transactions on My expenses only.',
  manager:
    'Read-only across operations modules. NO access to financial reports or bank balances. Sees own (and assigned team) card transactions on My expenses.',
  employee:
    'Write on own assigned tasks. Sees own card transactions on My expenses. NO access to financial reports, bank data, or admin pages unless a super admin grants a section (for example Integrations).',
  external_lender:
    'Outside party (e.g. a family lender) who can VIEW only the specific loans they funded. No dashboard, no financials, no operations — read-only access to the Loans pages they are scoped into.',
};

/**
 * Module-level permission map. `true` means the role can READ that module.
 *
 * SECURITY NOTE — financials lockdown:
 * Anything that surfaces money flowing through Mercury or QuickBooks
 * (bank accounts, transactions, P&L, balance sheet, cash flow, AR/AP,
 * chart of accounts, and audit log diffs of those tables) is gated to
 * `super_admin` ONLY. The Integrations page is separately grantable per
 * person (see APP_SECTIONS). The DB enforces the financials restriction
 * via RLS in supabase/migrations/15_financials_super_admin_only.sql —
 * do NOT loosen one side without loosening the other.
 */
export const MODULE_READ: Record<Role, Record<string, boolean>> = {
  super_admin: {
    dashboard: true,
    calendar: true,
    // `digital_office` is Digital Company, the Sims-style virtual building
    // under Overview. Same audience as `calendar`: every org member, no
    // external lenders.
    digital_office: true,
    pto: true,
    // `hr` covers handbook, policies, and personal employment
    // documents (I-9, tax forms, etc.). Payroll stays in Gusto —
    // this module is everything else people-ops. Same audience as
    // `pto` today (every non-lender member).
    hr: true,
    financials: true,
    // `loans` is a sub-section of financials surfaced as its own module
    // so external lenders can be granted loan-only visibility without
    // pulling in bank balances, P&L, or anything else under financials.
    // Super admin keeps both flags on.
    loans: true,
    expenses: true,
    integrations: true,
    inventory: true,
    catalog: true,
    procurement: true,
    manufacturing: true,
    // `work_mode` is the station-tablet surface at /work (migration 116).
    // It is deliberately its own flag rather than riding on
    // `manufacturing`: the Manufacturing *section* (BOMs, work orders,
    // MRP) is something a super admin will often switch off for a floor
    // employee, and that must never take the tablet away from them. The
    // DB side already lets any active org member use the work_* RPCs;
    // worker identity comes from the PIN / badge, not the account.
    work_mode: true,
    sales: true,
    sops: true,
    marketing: true,
    intelligence: true,
    projects: true,
    // `tickets` is the request inbox under Tasks (migration 143). Role-only
    // like `work_mode`: switching the Project Management section off for
    // someone must never take their ticket inbox away — a floor employee
    // with no projects still files and follows requests.
    tickets: true,
    // Engineering projects, the versioned CAD / drawing drive, the 3D print
    // log and engineering budgets. Same audience as `projects` (every org
    // member, no external lenders) so production and management can look
    // up a part's engineering files; the project team controls writes.
    engineering: true,
    // Shared company documents: templates, profile, licenses, contracts.
    // Not the locked Settings section (roles, audit, integrations).
    company_admin: true,
    users: true,
    audit: true,
  },
  admin: {
    dashboard: true,
    calendar: true,
    digital_office: true,
    pto: true,
    hr: true,
    financials: false,
    loans: false,
    expenses: true,
    integrations: false,
    inventory: true,
    catalog: true,
    procurement: true,
    manufacturing: true,
    work_mode: true,
    sales: true,
    sops: true,
    marketing: true,
    intelligence: true,
    projects: true,
    tickets: true,
    engineering: true,
    company_admin: true,
    users: false,
    audit: false,
  },
  manager: {
    dashboard: true,
    calendar: true,
    digital_office: true,
    pto: true,
    hr: true,
    financials: false,
    loans: false,
    expenses: true,
    integrations: false,
    inventory: true,
    catalog: true,
    procurement: true,
    manufacturing: true,
    work_mode: true,
    sales: true,
    sops: true,
    marketing: true,
    intelligence: true,
    projects: true,
    tickets: true,
    engineering: true,
    company_admin: true,
    users: false,
    audit: false,
  },
  employee: {
    dashboard: true,
    calendar: true,
    digital_office: true,
    pto: true,
    hr: true,
    financials: false,
    loans: false,
    expenses: true,
    integrations: false,
    inventory: true,
    catalog: true,
    procurement: false,
    manufacturing: true,
    // Floor workers are the primary Work Mode audience — stays on even
    // when the Manufacturing section is revoked for this person.
    work_mode: true,
    sales: false,
    sops: true,
    marketing: false,
    intelligence: false,
    projects: true,
    tickets: true,
    engineering: true,
    company_admin: true,
    users: false,
    audit: false,
  },
  // External lender: the most locked-down tier in the app. Loans only —
  // and per-loan visibility is layered on top by the sidebar / route
  // gates (e.g. SBA loan stays super_admin-only). NO dashboard so the
  // root redirect drops them straight on their loan page rather than
  // on a dashboard full of widgets they can't populate.
  external_lender: {
    dashboard: false,
    calendar: false,
    digital_office: false,
    pto: false,
    hr: false,
    financials: false,
    loans: true,
    expenses: false,
    integrations: false,
    inventory: false,
    catalog: false,
    procurement: false,
    manufacturing: false,
    work_mode: false,
    sales: false,
    sops: false,
    marketing: false,
    intelligence: false,
    projects: false,
    tickets: false,
    engineering: false,
    company_admin: false,
    users: false,
    audit: false,
  },
};

/**
 * Action-level permission for module mutations.
 *
 * `expenses` is `true` for everyone because the Expense Tracking page allows
 * employees to fill out their own card swipes (explanation, receipt,
 * category, submit-for-approval). The DB enforces row-level scoping via
 * card_assignments and column-level scoping via
 * tg_transactions_employee_field_guard — this UI flag is only the surface
 * "is the page in read-only mode" toggle.
 */
export const MODULE_WRITE: Record<Role, Record<string, boolean>> = {
  super_admin: {
    calendar: true,
    pto: true,
    hr: true,
    financials: true,
    loans: true,
    expenses: true,
    integrations: true,
    inventory: true,
    catalog: true,
    procurement: true,
    manufacturing: true,
    sales: true,
    sops: true,
    projects: true,
    tickets: true,
    engineering: true,
    company_admin: true,
    users: true,
  },
  admin: {
    calendar: true,
    pto: true,
    hr: true,
    financials: false,
    loans: false,
    expenses: true,
    integrations: false,
    inventory: true,
    catalog: true,
    procurement: true,
    manufacturing: true,
    sales: true,
    sops: true,
    projects: true,
    tickets: true,
    engineering: true,
    company_admin: true,
    users: false,
  },
  manager: {
    calendar: true,
    pto: true,
    hr: true,
    financials: false,
    loans: false,
    expenses: true,
    integrations: false,
    inventory: false,
    catalog: false,
    procurement: false,
    manufacturing: true,
    sales: false,
    sops: false,
    projects: true,
    tickets: true,
    // Any team member can start an engineering project and work on the
    // projects they are assigned to (RLS scopes writes to the project team).
    engineering: true,
    company_admin: false,
    users: false,
  },
  employee: {
    calendar: true,
    pto: true,
    hr: true,
    financials: false,
    loans: false,
    expenses: true,
    integrations: false,
    inventory: false,
    catalog: false,
    procurement: false,
    manufacturing: false, // employees write WO progress only, narrower than module write
    sales: false,
    sops: false,
    projects: true, // anyone can create projects + edit tasks they're a member of
    tickets: true, // anyone files requests; the DB trigger limits what a requester may edit
    engineering: true, // same model: create projects, edit the ones you're on
    company_admin: false,
    users: false,
  },
  // External lender is strictly view-only. Storage RLS mirrors this:
  // migration 60 grants SELECT on loan-receipts but no INSERT / UPDATE /
  // DELETE, so even if the UI guard were bypassed the bucket itself
  // would reject the write.
  external_lender: {
    calendar: false,
    pto: false,
    hr: false,
    financials: false,
    loans: false,
    expenses: false,
    integrations: false,
    inventory: false,
    catalog: false,
    procurement: false,
    manufacturing: false,
    sales: false,
    sops: false,
    projects: false,
    tickets: false,
    engineering: false,
    company_admin: false,
    users: false,
  },
};

/**
 * Sidebar sections a super_admin can grant or revoke per person.
 * `locked` sections stay role-only (financials / administration) so an
 * accidental toggle can't punch through bank data, user-admin, or the
 * audit log. Integrations is grantable on its own so an employee can
 * connect Shopify / ads without seeing Roles & users.
 */
export type SectionId =
  | 'overview'
  | 'hr'
  | 'projects'
  | 'engineering'
  | 'financials'
  | 'catalog'
  | 'inventory'
  | 'procurement'
  | 'manufacturing'
  | 'sales'
  | 'sops'
  | 'marketing'
  | 'intelligence'
  | 'integrations'
  | 'company_admin'
  | 'administration';

export type SectionAccess = Partial<Record<SectionId, boolean>>;

export const APP_SECTIONS: Array<{
  id: SectionId;
  label: string;
  description: string;
  modules: string[];
  locked: boolean;
}> = [
  { id: 'overview', label: 'Overview', description: 'Dashboard, calendar, and Digital Company', modules: ['dashboard', 'calendar', 'digital_office'], locked: false },
  { id: 'hr', label: 'HR', description: 'PTO, org chart, handbook, reviews', modules: ['hr', 'pto'], locked: false },
  // Section id stays `projects` so saved section_access rows keep working.
  // The ticket inbox sits in the same sidebar section but is the role-only
  // `tickets` module (see MODULE_SECTION), so revoking Tasks never hides it.
  { id: 'projects', label: 'Tasks', description: 'Task lists and to-dos across departments. The ticket inbox is always on.', modules: ['projects'], locked: false },
  { id: 'engineering', label: 'Engineering', description: 'Engineering projects, CAD file drive, 3D print log', modules: ['engineering'], locked: false },
  { id: 'financials', label: 'Financials', description: 'Banking, actuals, projections, loans', modules: ['financials', 'loans'], locked: true },
  { id: 'catalog', label: 'Catalog', description: 'Items, equipment, software, customers, suppliers', modules: ['catalog'], locked: false },
  { id: 'inventory', label: 'Inventory', description: 'On-hand, locations, movements', modules: ['inventory'], locked: false },
  { id: 'procurement', label: 'Procurement', description: 'Procurement calendar, purchase orders, receipts', modules: ['procurement'], locked: false },
  { id: 'manufacturing', label: 'Manufacturing', description: 'BOMs, work orders, production planning', modules: ['manufacturing'], locked: false },
  { id: 'sales', label: 'Sales', description: 'Orders, quotes, shipments, returns', modules: ['sales'], locked: false },
  { id: 'sops', label: 'Quality / ISO', description: 'Documents, training, audits', modules: ['sops'], locked: false },
  { id: 'marketing', label: 'Marketing', description: 'Sales & marketing dashboards, ads, social', modules: ['marketing'], locked: false },
  { id: 'intelligence', label: 'Intelligence', description: 'Forecast and KPIs', modules: ['intelligence'], locked: false },
  {
    id: 'company_admin',
    label: 'Admin',
    description: 'Templates, company profile, licenses, contracts, and contacts',
    modules: ['company_admin'],
    locked: false,
  },
  {
    id: 'integrations',
    label: 'Integrations',
    description: 'Connect Shopify, ads, analytics, and other providers. Does not include Roles & users, card mapping, or the audit log.',
    modules: ['integrations'],
    locked: false,
  },
  { id: 'administration', label: 'Settings', description: 'Roles & users, audit log', modules: ['users', 'audit'], locked: true },
];

const SECTION_IDS = new Set<string>(APP_SECTIONS.map((s) => s.id));

/**
 * Module → grantable sidebar section. Modules missing from this map
 * (today: `work_mode` and `tickets`) are role-only: `canRead` falls
 * straight through to MODULE_READ and no per-person section toggle can
 * hide them. Work Mode is kept out on purpose so revoking
 * "Manufacturing" from a floor employee never locks them out of their
 * station tablet; the ticket inbox likewise survives revoking "Tasks".
 */
const MODULE_SECTION: Record<string, SectionId> = {
  dashboard: 'overview',
  calendar: 'overview',
  digital_office: 'overview',
  pto: 'hr',
  hr: 'hr',
  projects: 'projects',
  engineering: 'engineering',
  financials: 'financials',
  loans: 'financials',
  expenses: 'financials',
  catalog: 'catalog',
  inventory: 'inventory',
  procurement: 'procurement',
  manufacturing: 'manufacturing',
  sales: 'sales',
  sops: 'sops',
  marketing: 'marketing',
  intelligence: 'intelligence',
  company_admin: 'company_admin',
  users: 'administration',
  integrations: 'integrations',
  audit: 'administration',
};

export function parseSectionAccess(raw: unknown): SectionAccess {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: SectionAccess = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (SECTION_IDS.has(key) && typeof value === 'boolean') {
      out[key as SectionId] = value;
    }
  }
  return out;
}

export function sectionDefault(role: Role | null, sectionId: SectionId): boolean {
  if (!role) return false;
  if (role === 'super_admin') return true;
  const spec = APP_SECTIONS.find((s) => s.id === sectionId);
  if (!spec) return false;
  return spec.modules.some((module) => MODULE_READ[role]?.[module] ?? false);
}

export function sectionEnabled(
  role: Role | null,
  sectionId: SectionId,
  sectionAccess?: SectionAccess | null,
): boolean {
  if (!role) return false;
  if (role === 'super_admin') return true;
  const spec = APP_SECTIONS.find((s) => s.id === sectionId);
  if (!spec) return false;
  if (!spec.locked && sectionAccess && typeof sectionAccess[sectionId] === 'boolean') {
    return sectionAccess[sectionId] === true;
  }
  return sectionDefault(role, sectionId);
}

export function nextSectionAccess(
  current: SectionAccess,
  role: Role,
  sectionId: SectionId,
  enabled: boolean,
): SectionAccess {
  const next = { ...current };
  if (enabled === sectionDefault(role, sectionId)) {
    delete next[sectionId];
  } else {
    next[sectionId] = enabled;
  }
  return next;
}

export function canRead(
  role: Role | null,
  module: string,
  sectionAccess?: SectionAccess | null,
): boolean {
  if (!role) return false;
  const sectionId = MODULE_SECTION[module];
  if (!sectionId) return MODULE_READ[role]?.[module] ?? false;
  const spec = APP_SECTIONS.find((s) => s.id === sectionId);
  if (spec?.locked || role === 'super_admin') {
    return MODULE_READ[role]?.[module] ?? false;
  }
  if (sectionAccess && typeof sectionAccess[sectionId] === 'boolean') {
    return sectionAccess[sectionId] === true;
  }
  return MODULE_READ[role]?.[module] ?? false;
}

export function canWrite(role: Role | null, module: string): boolean {
  if (!role) return false;
  return MODULE_WRITE[role]?.[module] ?? false;
}

/** Sidebar / command-palette predicate that honors per-person section grants. */
export function vis(module: string) {
  return (role: Role | null, sectionAccess?: SectionAccess | null) =>
    canRead(role, module, sectionAccess);
}

export function isFinancialsFull(role: Role | null): boolean {
  return role === 'super_admin';
}

export function isSuperAdmin(role: Role | null): boolean {
  return role === 'super_admin';
}

/**
 * PTO approver tier — can read every PTO request in the org and
 * approve / reject them. Mirrors `public.pto_can_approve()` in
 * supabase/migrations/95_pto_tracking.sql.
 */
export function canApprovePto(role: Role | null): boolean {
  return role === 'super_admin' || role === 'admin' || role === 'manager';
}

/**
 * PTO awards tier — can set the per-person annual PTO allotment.
 * Mirrors `public.pto_can_manage_awards()` in migration 95.
 */
export function canManagePtoAwards(role: Role | null): boolean {
  return role === 'super_admin' || role === 'admin';
}

/**
 * Master switch for the financials MFA lockout.
 *
 * When `true`:
 *   - `requiresMfa()` returns true for any role with financials / audit /
 *     integrations access (today: `super_admin`).
 *   - `RequireMfa` redirects those users to /auth/mfa/setup or
 *     /auth/mfa/challenge until the session is AAL2.
 *   - The Topbar shows a red "Action needed" badge on the Security menu
 *     until the user enrols + verifies a phone factor.
 *   - The DB RLS policies in `supabase/migrations/23_mfa_financials.sql`
 *     refuse to return rows unless the JWT carries `aal=aal2`.
 *
 * When `false` (current state):
 *   - The four UI surfaces above all fall through to their "optional"
 *     branches — Atlas no longer locks anyone out for not having SMS
 *     2-step verification on file.
 *   - The MFA enrol / challenge / settings pages remain fully wired up
 *     so users can still voluntarily add a phone factor, and the
 *     supabase-js helpers in `src/features/auth/mfa.ts` still work.
 *   - The DB-side AAL2 gate is replaced by the plain super_admin-only
 *     policies from migration 15 (see migration 24).
 *
 * The lockout is disabled because Supabase phone MFA requires the Pro
 * plan + a Twilio account, and that recurring cost isn't justified yet.
 * Flip back to `true` AND re-apply migration 23 (or an equivalent
 * AAL2-enforcing migration) to bring the lockout back.
 *
 * Database mirror when enabled: `public.role_requires_mfa(public.user_role)`.
 */
export const MFA_LOCKOUT_ENABLED = false;

/**
 * True when this role has access to *anything financials* (bank balances,
 * transactions, P&L, balance sheet, cash flow, AR/AP, chart of accounts,
 * integrations management, or the audit log — which leaks the same data
 * through before/after diffs) AND the global MFA lockout is currently
 * enabled.
 *
 * NOTE — Expense Tracking is intentionally *not* covered. It surfaces a
 * user's own card swipes for receipt/category fill-in and is not classed
 * as a financials surface. If product ever decides otherwise, flip this
 * to also include `canRead(role, 'expenses')`.
 */
export function requiresMfa(role: Role | null): boolean {
  if (!MFA_LOCKOUT_ENABLED) return false;
  if (!role) return false;
  return canRead(role, 'financials') || canRead(role, 'audit') || canRead(role, 'integrations');
}
